'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useTheme } from 'next-themes'

import { talentPoolCopy } from '@/app/talent-pool/copy'
import { pushTalentPoolGtmEvent } from './talent-pool-analytics'
import { useTalentPoolSubmissionState } from './talent-pool-submission-state'
import {
  TALENT_POOL_SUBMISSION_TTL_MS,
  useTalentPoolStore,
} from '@/app/talent-pool/talent-pool-store'
import { useTalentPoolUploadState } from './talent-pool-upload-state'

export { type UploadPickerTrigger } from './talent-pool-page-types'

export const useTalentPoolPageState = () => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const resolvedSearchParams = searchParams
  const { resolvedTheme } = useTheme()
  const copy = talentPoolCopy

  const [mounted, setMounted] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const oauthCallbackTrackedRef = useRef(false)

  const hasHydrated = useTalentPoolStore((s) => s.hasHydrated)
  const draft = useTalentPoolStore((s) => s.draft)
  const storedFileName = useTalentPoolStore((s) => s.storedFileName)
  const submittedAt = useTalentPoolStore((s) => s.submittedAt)
  const setDraft = useTalentPoolStore((s) => s.setDraft)
  const setStoredFileName = useTalentPoolStore((s) => s.setStoredFileName)
  const markSubmitted = useTalentPoolStore((s) => s.markSubmitted)
  const pruneExpiredSubmission = useTalentPoolStore(
    (s) => s.pruneExpiredSubmission,
  )

  const isResume = resolvedSearchParams.get('resume') === 'true'
  const shouldAutoSubmit = resolvedSearchParams.get('auto-submit') === 'true'
  const hasThankYou = resolvedSearchParams.has('thank-you')
  const oauthStatus =
    resolvedSearchParams.get('oauth-status') ?? resolvedSearchParams.get('oauth_status')
  const oauthReason =
    resolvedSearchParams.get('oauth-reason') ?? resolvedSearchParams.get('oauth_reason')

  const hasPersistedSubmission =
    submittedAt !== null && Date.now() - submittedAt < TALENT_POOL_SUBMISSION_TTL_MS
  const shouldShowThankYou = hasThankYou || (hasHydrated && hasPersistedSubmission)

  const logoSrc =
    resolvedTheme === 'dark' && mounted
      ? 'https://hyred.sgp1.cdn.digitaloceanspaces.com/assets/dark-logo.svg'
      : 'https://hyred.sgp1.cdn.digitaloceanspaces.com/assets/logo.svg'

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    pruneExpiredSubmission()
  }, [pruneExpiredSubmission])

  useEffect(() => {
    if (oauthCallbackTrackedRef.current) return
    if (oauthStatus !== 'success' && oauthStatus !== 'error') return

    oauthCallbackTrackedRef.current = true

    pushTalentPoolGtmEvent('talent_pool_oauth_google_callback', {
      page: 'talent-pool',
      placement: 'oauth_callback',
      status: oauthStatus,
      reason: oauthReason ?? null,
    })

    if (oauthStatus === 'success') {
      pushTalentPoolGtmEvent('talent_pool_oauth_google_callback_success', {
        page: 'talent-pool',
        placement: 'oauth_callback',
      })
      return
    }

    pushTalentPoolGtmEvent('talent_pool_oauth_google_callback_error', {
      page: 'talent-pool',
      placement: 'oauth_callback',
      reason: oauthReason ?? 'unknown',
    })
  }, [oauthReason, oauthStatus])

  const upload = useTalentPoolUploadState({
    validationCopy: copy.validation,
    storedFileName,
    setStoredFileName,
    setSubmitError,
  })

  const submission = useTalentPoolSubmissionState({
    copy,
    draft,
    file: upload.file,
    storedFileName,
    setFile: upload.setFile,
    setStoredFileName,
    markSubmitted,
    hasHydrated,
    hasPersistedSubmission,
    isResume,
    shouldAutoSubmit,
    oauthStatus,
    oauthReason,
    setSubmitError,
    routerPush: router.push,
  })

  const scrollToUpload = useCallback(() => {
    const element = document.getElementById('talent-pool-upload')
    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const isLanding = submission.step === 'landing'
  const mainMaxWidth = isLanding ? 'max-w-6xl' : 'max-w-lg'

  return {
    copy,
    logoSrc,
    hasHydrated,
    hasThankYou,
    shouldShowThankYou,
    draft,
    setDraft,
    storedFileName,
    submitError,
    setSubmitError,
    scrollToUpload,
    mainMaxWidth,
    isLanding,
    fileName: upload.file?.name ?? storedFileName ?? null,
    file: upload.file,
    isDragging: upload.isDragging,
    canGoToPersonal: upload.canGoToPersonal,
    fileInputRef: upload.fileInputRef,
    handleUploadAreaDragOver: upload.handleUploadAreaDragOver,
    handleUploadAreaDragLeave: upload.handleUploadAreaDragLeave,
    handleUploadAreaDrop: upload.handleUploadAreaDrop,
    openFilePicker: upload.openFilePicker,
    handleUploadAreaKeyDown: upload.handleUploadAreaKeyDown,
    handleFileInputChange: upload.handleFileInputChange,
    removeFile: upload.removeFile,
    step: submission.step,
    isFormStep: submission.isFormStep,
    stepIndex: submission.stepIndex,
    stepLabels: submission.stepLabels,
    fieldErrors: submission.fieldErrors,
    setFieldErrors: submission.setFieldErrors,
    isSubmitting: submission.isSubmitting,
    isAuthenticating: submission.isAuthenticating,
    goBack: submission.goBack,
    goNext: submission.goNext,
    handleLandingNext: submission.handleLandingNext,
    handleSubmit: submission.handleSubmit,
  }
}
